// This asset loads before AOV's deferred storefront script. Keep the theme's
// original fetch so the native product button can add a single variant without
// being rewritten into the currently selected AOV bundle.
window.__marsworkFetchBeforeAov =
  window.__marsworkFetchBeforeAov || window.fetch.bind(window);

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector("cart-drawer");
        this.hideErrors = this.dataset.hideErrors === "true";

        if (this.querySelector("form")) {
          this.form = this.querySelector("form");
          const variantInput = this.form.querySelector("[name=id]");
          if (variantInput) variantInput.disabled = false;
          this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        } else {
          const variantInput = this.querySelector("[name=id]");
          if (variantInput) variantInput.disabled = false;
          const submitBtn = this.querySelector("button[type=submit]");
          if (submitBtn) {
            submitBtn.addEventListener(
              "click",
              this.onSubmitHandler.bind(this),
            );
          }
        }

        this.refreshSubmitElements();
        this.observeSubmitButtonState();
        this.onAovSubmitButtonClick = this.onAovSubmitButtonClick.bind(this);
        this.observeAovBundleState();

        const productInfo = this.closest(".product__info-container");
        const sectionId = productInfo?.dataset.section;
        this.productAdditionalService = productInfo?.querySelector(
          `product-additional-service[data-section-id="${sectionId}"]`,
        );
      }

      refreshSubmitElements() {
        this.submitButton =
          this.querySelector('[type="submit"][name="add"]') ||
          this.querySelector('button[name="add"]') ||
          this.querySelector('[type="submit"]');
        this.submitSpinner =
          this.submitButton?.querySelector(".loading-overlay__spinner") ||
          this.querySelector(".loading-overlay__spinner");

        if (document.querySelector("cart-drawer") && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }
      }

      getAovBundleOffer() {
        const productInfo = this.closest(".product__info-container");
        if (!productInfo) return null;

        return productInfo.querySelector(
          ".Avada-Bundle-Offer__Volume [data-offer-id]",
        );
      }

      isAovBundleManagingSubmit() {
        const offer = this.getAovBundleOffer();
        if (!offer) return false;

        return !!offer.querySelector(
          '.Avada-Volume__Item--Selected, [role="radio"][aria-checked="true"]',
        );
      }

      getAovSelectedVariantId() {
        const offer = this.getAovBundleOffer();
        const selectedOffer = offer?.querySelector(
          '.Avada-Volume__Item--Selected, [role="radio"][aria-checked="true"]',
        );
        const selectedProduct = selectedOffer?.querySelector(
          ".Avada-Volume__ChooseSlot--Filled",
        );
        if (!selectedProduct) return "";

        const selectedOptions = Array.from(
          selectedProduct.querySelectorAll('[role="combobox"][aria-label]'),
        ).map((option) => {
          const label = option.getAttribute("aria-label") || "";
          const separatorIndex = label.indexOf(":");
          return separatorIndex === -1
            ? label.trim()
            : label.slice(separatorIndex + 1).trim();
        });
        if (
          !selectedOptions.length ||
          selectedOptions.some((value) => !value)
        ) {
          return "";
        }

        const variantPicker = this.getVariantPicker();
        const variantsJson = variantPicker?.querySelector(
          "script[data-all-variants-no-high]",
        );
        if (!variantsJson?.textContent) return "";

        try {
          const variants = JSON.parse(variantsJson.textContent);
          const selectedVariant = variants.find((variant) => {
            if (variant.available === false) return false;

            const variantOptions = Array.isArray(variant.options)
              ? variant.options
              : [variant.option1, variant.option2, variant.option3].filter(
                  (value) => value != null,
                );

            return selectedOptions.every(
              (value, index) => String(variantOptions[index]) === value,
            );
          });

          return selectedVariant?.id ? String(selectedVariant.id) : "";
        } catch (error) {
          console.error("Unable to resolve AOV bundle variant:", error);
          return "";
        }
      }

      getAovSubmitButton() {
        const productInfo = this.closest(".product__info-container");
        return productInfo?.querySelector(
          ".marswork-aov-submit-wrapper .marswork-aov-submit",
        );
      }

      ensureAovSubmitButton() {
        const offer = this.getAovBundleOffer();
        const selectedOffer = offer?.querySelector(
          '.Avada-Volume__Item--Selected, [role="radio"][aria-checked="true"]',
        );
        if (!selectedOffer) return null;

        const productInfo = this.closest(".product__info-container");
        let wrapper = productInfo?.querySelector(
          ".marswork-aov-submit-wrapper",
        );
        if (!wrapper) {
          wrapper = document.createElement("div");
          wrapper.className = "marswork-aov-submit-wrapper";

          const nativeSubmitButton = this.getSubmitButton();
          const nativeButtonStyle = [
            "button--primary",
            "button--secondary",
            "button--transparent",
          ].find((className) =>
            nativeSubmitButton?.classList.contains(className),
          );

          const button = document.createElement("button");
          button.type = "button";
          button.className = `button product-form__submit ${
            nativeButtonStyle || "button--primary"
          } marswork-aov-submit`;
          button.setAttribute("aria-haspopup", "dialog");

          const content = document.createElement("div");
          content.className = "button__content";

          const label = document.createElement("span");
          label.className = "button__label marswork-aov-submit-label";
          label.dataset.label = "Add Bundle to Cart";
          label.textContent = "Add Bundle to Cart";
          content.appendChild(label);
          button.appendChild(content);

          const animatedCircle = document.createElement("div");
          animatedCircle.className = "animated-button-circle";
          button.appendChild(animatedCircle);

          const spinner = document.createElement("div");
          spinner.className = "loading-overlay__spinner hidden";
          spinner.innerHTML = `
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="5" cx="33" cy="33" r="30"></circle>
            </svg>
          `;
          button.appendChild(spinner);

          button.addEventListener("click", this.onAovSubmitButtonClick);

          wrapper.appendChild(button);
        }

        if (wrapper.parentElement !== selectedOffer) {
          selectedOffer.appendChild(wrapper);
        }

        return wrapper.querySelector(".marswork-aov-submit");
      }

      syncAovSubmitButton() {
        const button = this.ensureAovSubmitButton();
        if (!button) return;

        const hasAvailableVariant = !!this.getAovSelectedVariantId();
        const shouldDisable = this.isSubmitting || !hasAvailableVariant;
        const status = this.isSubmitting
          ? "loading"
          : hasAvailableVariant
            ? "available"
            : "unavailable";

        if (button.disabled !== shouldDisable) {
          button.disabled = shouldDisable;
        }
        if (button.getAttribute("aria-disabled") !== String(shouldDisable)) {
          button.setAttribute("aria-disabled", String(shouldDisable));
        }
        if (button.dataset.status !== status) {
          button.dataset.status = status;
        }
        if (button.classList.contains("loading") !== !!this.isSubmitting) {
          button.classList.toggle("loading", !!this.isSubmitting);
        }

        button
          .querySelector(".loading-overlay__spinner")
          ?.classList.toggle("hidden", !this.isSubmitting);
      }

      onAovSubmitButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.isSubmitting) return;

        this.aovSubmitRequested = true;
        this.onSubmitHandler(event);
      }

      // AOV renders asynchronously and replaces its own nodes whenever a tier
      // or product changes. Keep the dedicated button inside the selected tier
      // and move it along when the customer chooses another tier.
      observeAovBundleState() {
        if (this.aovBundleStateObserver) return;

        const productInfo = this.closest(".product__info-container");
        if (!productInfo) return;

        const syncSubmitButton = () => {
          this.syncAovSubmitButton();
        };

        this.aovBundleStateObserver = new MutationObserver(syncSubmitButton);
        this.aovBundleStateObserver.observe(productInfo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class", "aria-checked"],
        });

        syncSubmitButton();
      }

      // AOV AI Bundle re-renders the product area after add-to-cart and can
      // write a stale disabled/aria-disabled snapshot back onto the submit
      // button while the variant is still purchasable. Because the disabled
      // styling uses pointer-events: none, no click can ever reach the form —
      // so watch the attributes and restore the clickable state directly.
      observeSubmitButtonState() {
        if (this.submitButtonStateObserver) return;

        this.submitButtonStateObserver = new MutationObserver(() => {
          if (this.isSubmitting) return;

          const submitButton = this.getSubmitButton();
          if (!submitButton) return;

          const status = submitButton.dataset?.status;
          if (
            status === "sold-out" ||
            status === "unavailable" ||
            status === "loading"
          ) {
            return;
          }

          const variantPicker = this.getVariantPicker();
          const variantId =
            variantPicker?.getResolvedVariantId?.() ||
            this.getVariantInput()?.value?.trim();
          if (!variantId) return;

          const input = this.getVariantInput();
          if (input?.disabled) return;

          if (
            variantPicker &&
            typeof variantPicker.isVariantValidForCart === "function" &&
            !variantPicker.isVariantValidForCart()
          ) {
            return;
          }

          if (
            submitButton.hasAttribute("disabled") ||
            submitButton.getAttribute("aria-disabled") === "true"
          ) {
            submitButton.removeAttribute("disabled");
            submitButton.removeAttribute("aria-disabled");
            submitButton.classList.remove("loading");
          }
        });

        const observeTarget = () => {
          const submitButton = this.getSubmitButton();
          if (submitButton) {
            this.submitButtonStateObserver.observe(submitButton, {
              attributes: true,
              attributeFilter: ["disabled", "aria-disabled"],
            });
          }
        };

        observeTarget();
        // The button node itself is replaced by app re-renders; re-attach on
        // DOM changes within the form.
        new MutationObserver(observeTarget).observe(this, {
          childList: true,
          subtree: true,
        });
      }

      getSubmitButton() {
        return (
          this.querySelector('[type="submit"][name="add"]') ||
          this.querySelector('button[name="add"]') ||
          this.querySelector('[type="submit"]')
        );
      }

      setSubmitLoading(isLoading, isAovSubmit = false) {
        if (isAovSubmit) {
          const aovSubmitButton = this.getAovSubmitButton();
          if (!aovSubmitButton) return;

          aovSubmitButton.setAttribute("aria-disabled", String(isLoading));
          aovSubmitButton.classList.toggle("loading", isLoading);
          aovSubmitButton.dataset.status = isLoading ? "loading" : "available";

          aovSubmitButton
            .querySelector(".loading-overlay__spinner")
            ?.classList.toggle("hidden", !isLoading);
          return;
        }

        const submitButton = this.getSubmitButton();
        const spinner =
          submitButton?.querySelector(".loading-overlay__spinner") ||
          this.querySelector(".loading-overlay__spinner");

        if (!submitButton) return;

        this.submitButton = submitButton;
        this.submitSpinner = spinner;

        if (isLoading) {
          submitButton.setAttribute("aria-disabled", "true");
          submitButton.classList.add("loading");
          spinner?.classList.remove("hidden");
          return;
        }

        submitButton.classList.remove("loading");
        submitButton.removeAttribute("aria-disabled");
        spinner?.classList.add("hidden");
      }

      getVariantPickerSectionId() {
        const source = this.dataset.source;
        if (!source) return null;
        return source.replace(/-floating$/, "");
      }

      getVariantPicker() {
        const sectionId = this.getVariantPickerSectionId();
        if (!sectionId) return null;

        const quickAddModal = this.closest("quick-add-modal");
        const searchRoot = quickAddModal || document;

        return (
          searchRoot.querySelector(`#variant-picker-${sectionId}`) ||
          searchRoot.querySelector(
            `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`,
          ) ||
          (!quickAddModal &&
            document.querySelector(
              `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`,
            ))
        );
      }

      getVariantInput() {
        if (!this.form) {
          this.form = this.querySelector("form");
        }
        return (
          this.form?.querySelector('[name="id"]') ||
          this.querySelector('[name="id"]')
        );
      }

      canSubmitToCart(isAovSubmit = false) {
        if (!this.form) {
          this.form = this.querySelector("form");
        }

        const submitButton = this.getSubmitButton();
        const variantPicker = this.getVariantPicker();
        const variantInput = this.getVariantInput();

        if (variantPicker?.productInfoLoading) return false;

        if (isAovSubmit) {
          const aovVariantId = this.getAovSelectedVariantId();
          const aovSubmitButton = this.getAovSubmitButton();
          if (!aovVariantId || variantInput?.disabled || !aovSubmitButton) {
            return false;
          }

          return !(
            aovSubmitButton.hasAttribute("disabled") ||
            aovSubmitButton.getAttribute("aria-disabled") === "true" ||
            aovSubmitButton.dataset?.status === "loading"
          );
        }

        const variantId =
          variantPicker?.getResolvedVariantId?.() ||
          variantInput?.value?.trim() ||
          "";

        if (!variantId || variantInput?.disabled) return false;

        if (!submitButton) return false;

        const buttonStatus = submitButton.dataset?.status;
        if (
          buttonStatus === "unavailable" ||
          buttonStatus === "sold-out" ||
          buttonStatus === "loading"
        ) {
          return false;
        }

        if (
          variantPicker &&
          typeof variantPicker.isVariantValidForCart === "function" &&
          !variantPicker.isVariantValidForCart()
        ) {
          return false;
        }

        if (
          submitButton.hasAttribute("disabled") ||
          submitButton.getAttribute("aria-disabled") === "true"
        ) {
          return false;
        }

        return true;
      }

      syncFormBeforeSubmit(isAovSubmit = false) {
        this.form = this.querySelector("form");
        const variantPicker = this.getVariantPicker();
        const variantInput = this.getVariantInput();

        if (isAovSubmit) {
          const aovVariantId = this.getAovSelectedVariantId();
          if (aovVariantId && variantInput) {
            variantInput.value = aovVariantId;
            variantInput.disabled = false;
          }
          this.normalizeQuantityBeforeSubmit();
          return;
        }

        if (
          variantPicker &&
          typeof variantPicker.syncAddToCartState === "function"
        ) {
          variantPicker.syncAddToCartState();
        }

        const submitButton = this.getSubmitButton();
        if (
          submitButton &&
          submitButton.dataset?.status === "loading" &&
          !variantPicker?.productInfoLoading
        ) {
          submitButton.removeAttribute("disabled");
          submitButton.removeAttribute("aria-disabled");
          submitButton.classList.remove("loading");
          submitButton.dataset.status = "available";
        }

        const variantId = variantPicker?.getResolvedVariantId?.();

        if (variantId && variantInput) {
          variantInput.value = variantId;
          variantInput.disabled = false;
        }

        this.normalizeQuantityBeforeSubmit();
      }

      normalizeQuantityBeforeSubmit() {
        const quantityInput =
          this.form?.querySelector('input[name="quantity"]') ||
          this.querySelector('input[name="quantity"]');
        if (quantityInput) {
          const min = Math.max(
            1,
            Number(
              quantityInput.getAttribute("min") ||
                quantityInput.dataset?.min ||
                1,
            ),
          );
          if (Number(quantityInput.value) < min) {
            quantityInput.value = String(min);
          }
        }
      }

      onSubmitHandler(evt) {
        const isAovSubmit = this.aovSubmitRequested === true;
        this.aovSubmitRequested = false;
        evt.preventDefault();
        this.syncFormBeforeSubmit(isAovSubmit);
        if (!this.canSubmitToCart(isAovSubmit)) return;

        this.handleErrorMessage();
        this.error = false;
        this.setSubmitLoading(true, isAovSubmit);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form || this);
        if (!this.form) {
          const variantInput = this.getVariantInput();
          if (variantInput?.value) {
            formData.set("id", variantInput.value);
          }
        }

        const quantity = Number(formData.get("quantity") || 1);
        if (!quantity || quantity < 1) {
          formData.set("quantity", "1");
        }

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        if (
          this.productAdditionalService &&
          this.productAdditionalService instanceof ProductAdditionalService &&
          typeof this.productAdditionalService.getItems === "function" &&
          Array.isArray(ProductAdditionalService.selectedServices) &&
          ProductAdditionalService.selectedServices.length > 0
        ) {
          const items = this.productAdditionalService.getItems(formData);
          if (items?.length > 0) {
            const bodyData = {
              items: items,
            };
            if (this.cart) {
              bodyData.sections = this.cart
                .getSectionsToRender()
                .map((section) => section.id);
              bodyData.sections_url = window.location.pathname;
            }

            delete config.headers["X-Requested-With"];
            config.headers["Content-Type"] = "application/json";
            config.body = JSON.stringify(bodyData);
          }
        }

        this.isSubmitting = true;

        const cartAddFetch =
          !isAovSubmit && this.isAovBundleManagingSubmit()
            ? window.__marsworkFetchBeforeAov
            : window.fetch.bind(window);

        cartAddFetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((rawResponse) => {
            // AOV AI Bundle wraps window.fetch and rewrites /cart/add
            // responses (its own JSON shape, or a {status: ...} object that is
            // not a real error). Those payloads used to be read as errors,
            // leaving the button disabled and the drawer closed. Rebuild the
            // payload from /cart.js whenever the drawer sections are missing.
            const hasErrorPayload =
              rawResponse?.status &&
              (rawResponse?.message || rawResponse?.description);

            if (
              rawResponse &&
              !hasErrorPayload &&
              !rawResponse.sections?.["cart-drawer"]
            ) {
              return fetch(
                `${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`,
              )
                .then((cartResponse) => cartResponse.json())
                .then((cartState) => {
                  if (!cartState?.sections?.["cart-drawer"]) return rawResponse;
                  return { ...rawResponse, ...cartState };
                })
                .catch(() => rawResponse);
            }

            return rawResponse;
          })
          .then((response) => {
            if (response.status && (response.message || response.description)) {
              document.dispatchEvent(
                new CustomEvent("cart:error", {
                  detail: {
                    source: "product-form",
                    productVariantId: formData.get("id"),
                    errors: response.description,
                    message: response.message,
                  },
                }),
              );

              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              const submitButton = this.getSubmitButton();
              const soldOutMessage =
                submitButton?.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              submitButton.setAttribute("aria-disabled", true);
              submitButton.querySelector("span")?.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              try {
                const cartUrl = new URL(
                  window.routes.cart_url,
                  window.location.origin,
                );
                const destination =
                  cartUrl.origin === window.location.origin
                    ? `${cartUrl.pathname}${cartUrl.search}${cartUrl.hash}`
                    : "/cart";
                window.location.assign(destination);
              } catch (error) {
                console.error("Invalid cart URL:", error);
                window.location.assign("/cart");
              }
              return;
            }

            if (!response.items?.length && !response.id) {
              // App-rewritten payloads may not expose items/id directly; the
              // drawer sections (already fetched above) are enough to treat
              // this as a successful cart update.
              if (!response.sections?.["cart-drawer"]) {
                this.handleErrorMessage(
                  response.description || response.message,
                );
                this.error = true;
                return;
              }
            }

            document.dispatchEvent(
              new CustomEvent("variant:add", {
                detail: {
                  variant: {
                    id: formData.get("id"),
                  },
                  quantity: Number(formData.get("quantity") || 1),
                  formElement: this.form || this,
                  sectionId: this.dataset.source,
                },
              }),
            );

            fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .then((parsedState) => {
                document.dispatchEvent(
                  new CustomEvent("cart:change", {
                    detail: {
                      cart: parsedState,
                      sectionId: this.dataset.source,
                    },
                  }),
                );
              })
              .catch((error) => {
                console.error("Error fetching cart state:", error);
              });

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
              });
              this.error = false;
            }

            const quickAddModal = this.closest("quick-add-modal");
            let activeElement = document.activeElement;

            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.setActiveElement(activeElement);
                    this.cart.renderContents(response, false);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
              if (
                this.productAdditionalService &&
                this.productAdditionalService instanceof
                  ProductAdditionalService &&
                typeof this.productAdditionalService.clearItems ===
                  "function" &&
                Array.isArray(ProductAdditionalService.selectedServices) &&
                ProductAdditionalService.selectedServices.length > 0
              ) {
                this.productAdditionalService.clearItems();
              }
            } else {
              if (this.closest(".header")) {
                const cartLink = document.querySelector("#cart-icon-bubble");
                if (cartLink) {
                  activeElement = cartLink;
                }
              } else if (this.closest(".collection-product-card")) {
                const productCard = this.closest(".collection-product-card");
                const linkTitle =
                  productCard?.querySelector(".card__title > a");
                if (linkTitle) {
                  activeElement = linkTitle;
                }
              }

              this.cart.setActiveElement(activeElement);
              this.cart.renderContents(response, false);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.setSubmitLoading(false, isAovSubmit);

            const submitButton = this.getSubmitButton();

            if (this.error) {
              if (!isAovSubmit) {
                submitButton?.setAttribute("aria-disabled", "true");
              }
              this.syncAovSubmitButton();
              return;
            }

            if (this.cart && this.cart.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }

            const variantPicker = this.getVariantPicker();
            if (
              variantPicker &&
              typeof variantPicker.syncAddToCartState === "function"
            ) {
              variantPicker.syncAddToCartState();
            }

            // The variant picker's branches (setUnavailable/setAddToCartPending)
            // can leave the button disabled; always restore a clickable state
            // after a successful add so the product can be added again.
            if (
              submitButton &&
              submitButton.dataset?.status !== "sold-out" &&
              submitButton.dataset?.status !== "unavailable"
            ) {
              submitButton.removeAttribute("disabled");
              submitButton.removeAttribute("aria-disabled");
              submitButton.classList.remove("loading");
              if (submitButton.dataset) {
                submitButton.dataset.status = "available";
              }
            }

            this.syncAovSubmitButton();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
